import React, { useEffect, useRef } from "react";
import Home from "./pages/Home";
import InformationAccount from "./pages/Registration/InformationAccount";
import DeviceVerification from "./pages/Verification/deviceVerification";
import PersonalInformation from "./pages/Registration/personalInformation";
import RegistrationEmail from "./pages/Registration/registrationEmail";
import AgreementPage from "./pages/KYC/AgreementPage";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Background from "./components/Background";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ForgetPassword from "./pages/forgetPassword";
import Dashboard from "./pages/pages/Dashboard";
import TradingInformation from "./pages/pages/Profile/TradingInfromation";
import FinancialInformation from "./pages/pages/Profile/FinancialInformation";
import ProfileInformation from "./pages/pages/Profile/ProfileInformation";
import UserRegulationPage from "./pages/pages/Profile/UserRegulation";
import CashIn from "./pages/pages/Funds/cachIn";
import CashOut from "./pages/pages/Funds/cachOut";
import Exchange from "./pages/pages/Funds/exchange";
import PayWithCard from "./pages/pages/Funds/payWithCard";
import InternalTransfer from "./pages/pages/Accounts/internalTransfer";
import DepositDemo from "./pages/pages/Accounts/depositDemo";
import AccountsList from "./pages/pages/Accounts/List";
import DemoAccounts from "./pages/pages/Accounts/DemoAccounts";
import AccountHistory from "./pages/pages/Accounts/AccountHistory";
import TransactionsHistory from "./pages/pages/Transactions/History";
import PendingTransactions from "./pages/pages/Transactions/Pending";
import CashInRequest from "./pages/pages/Transactions/cashInRequest";
import CashOutRequest from "./pages/pages/Transactions/cashOutRequest";
import IbDashboard from "./pages/pages/ibDashboard/dashboard";
import IbClientsList from "./pages/pages/ibDashboard/clientsList";
import IbClientsTree from "./pages/pages/ibDashboard/clientsTree";
import IbClientsAccounts from "./pages/pages/ibDashboard/clientsAccounts";
import TransferToClient from "./pages/pages/ibDashboard/transferToClient";
import TransferFromClient from "./pages/pages/ibDashboard/transferFromClient";
import CollectTransaction from "./pages/pages/ibDashboard/collectTransaction";
import SendNotification from "./pages/pages/ibDashboard/sendNotification";
import IbBrokerTools from "./pages/pages/ibDashboard/brokerTools";
import BrokerToolEditor from "./pages/pages/ibDashboard/BrokerToolEditor";
import MarketingTools from "./pages/pages/MarketingTools";
import ChangePin from "./pages/pages/Settings/changePin";
import ActiveDevices from "./pages/pages/Settings/activeDevices";
import LegalDocumentPage from "./pages/pages/Legal/LegalDocumentPage";
import SupportFaq from "./pages/pages/Support/Faq";
import SupportAboutUs from "./pages/pages/Support/AboutUs";
import ProtectedRoute from "./components/ProtectedRoute";
import FcmNotificationListener from "./components/FcmNotificationListener";
import PwaInstallBanner from "./components/PwaInstallBanner";
import rsaController from "./utils/RSA-controller";
import { clearHomePageInfosClientCache, deleteFcmTokenOnLogout } from "./utils/apiClient";
import { deleteWebFcmToken } from "./services/firebaseMessagingService";
import { resetUiPreferencesToDefaults } from "./utils/userPreferences";
import { useToast } from "./contexts/ToastContext";
import { PageTourProvider } from "./contexts/PageTourContext";
import {
  stashWhishReturn,
  consumeWhishReturn,
  parseWhishReturnParams,
  parseWhishReturnMessage,
  markWhishReturnActivity,
  claimWhishReturnHandling,
} from "./utils/whishReturn";
import "./App.css";

const SESSION_TIMEOUT_MS = 20 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "mfg_last_activity_at";

const hasActiveSession = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  return !!token && token !== "FIRST_TIME_LOGIN" && token.trim() !== "";
};

function KycBackRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    // If user is navigating back from the 3rd step of registration (/device-verification)
    // to the 2nd step (/personalInformation), send them to /home instead
    if (previousPath === "/device-verification" && currentPath === "/personalInformation") {
      navigate("/home", { replace: true });
    }

    previousPathRef.current = currentPath;
  }, [location.pathname, navigate]);

  return null;
}

/**
 * Handles Whish payment browser redirects and postMessage from the payment tab.
 * Logged-in users are sent to the dashboard.
 */
function WhishReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const handledKeyRef = useRef(null);

  useEffect(() => {
    const finishReturn = (result, externalId = "") => {
      if (!claimWhishReturnHandling(result, externalId)) return;
      const handleKey = `${result}:${externalId}`;
      if (handledKeyRef.current === handleKey) return;
      handledKeyRef.current = handleKey;

      stashWhishReturn(result, externalId);
      markWhishReturnActivity();

      if (!hasActiveSession()) {
        // Stay on login with the return query so Home can resume after sign-in
        navigate({ pathname: "/", search: location.search || "" }, { replace: true });
        return;
      }

      clearHomePageInfosClientCache();
      consumeWhishReturn();
      navigate("/pages/dashboard", { replace: true });

      if (result === "success") {
        showSuccess(
          t("transaction_succeeded", {
            ns: "mobile",
            defaultValue: "Transaction succeeded.",
          })
        );
      } else {
        showError(
          t("transaction_failed", {
            ns: "mobile",
            defaultValue: "Transaction failed.",
          })
        );
      }

      try {
        window.dispatchEvent(new Event("mfg:funds-refresh"));
        window.dispatchEvent(new Event("dashboard:refresh"));
      } catch (_) {}
    };

    const fromUrl = parseWhishReturnParams(location.search);
    if (fromUrl) {
      finishReturn(fromUrl.result, fromUrl.externalId);
    }

    const onMessage = (event) => {
      const fromMessage = parseWhishReturnMessage(event);
      if (fromMessage) {
        finishReturn(fromMessage.result, fromMessage.externalId);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [location.pathname, location.search, navigate, showSuccess, showError, t]);

  return null;
}

export default function App() {
  const navigate = useNavigate();

  useEffect(() => {
    const isElementVisible = (element) => {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        style.opacity !== "0"
      );
    };

    const getTopmostDialog = () => {
      const dialogs = Array.from(
        document.querySelectorAll(".popup-overlay, .transaction-password-overlay, .cts-dialog-overlay")
      ).filter(isElementVisible);
      return dialogs.length ? dialogs[dialogs.length - 1] : null;
    };

    const clickFirstEnabled = (root, selectors) => {
      for (const selector of selectors) {
        const candidate = root.querySelector(selector);
        if (candidate && !candidate.disabled) {
          candidate.click();
          return true;
        }
      }
      return false;
    };

    const handleShortcuts = (event) => {
      if (event.defaultPrevented || event.isComposing || event.repeat) return;

      const activeElement = document.activeElement;
      const activeTag = activeElement?.tagName?.toLowerCase();
      const isTextArea = activeTag === "textarea";
      const isContentEditable = !!activeElement?.isContentEditable;
      const isDropdownOpen =
        !!document.querySelector(".custom-dropdown-menu") ||
        activeElement?.getAttribute("aria-expanded") === "true";

      if (event.key === "Escape") {
        const topDialog = getTopmostDialog();
        if (!topDialog) return;

        event.preventDefault();

        if (topDialog.classList.contains("popup-overlay")) {
          topDialog.click();
          return;
        }

        if (topDialog.classList.contains("transaction-password-overlay")) {
          topDialog.click();
          return;
        }

        clickFirstEnabled(topDialog, [
          ".cts-dialog-close",
          ".cts-btn-cancel",
          ".popup-btn-cancel",
          "[aria-label='Close']",
          "button",
        ]);
        return;
      }

      if (event.key !== "Enter") return;
      if (isTextArea || isContentEditable || isDropdownOpen) return;

      const topDialog = getTopmostDialog();
      if (topDialog) {
        const handled = clickFirstEnabled(topDialog, [
          ".popup-btn-primary",
          ".transaction-password-apply-button",
          ".cts-btn-submit",
          "button[type='submit']",
        ]);
        if (handled) event.preventDefault();
        return;
      }

      // Page-level fallback (outside dialogs)
      const handled = clickFirstEnabled(document, [
        ".next-button:not(:disabled)",
        ".profile-btn-next:not(:disabled)",
        ".profile-btn-save:not(:disabled)",
        ".custom-button-primary:not(:disabled)",
        "button[type='submit']:not(:disabled)",
      ]);
      if (handled) event.preventDefault();
    };

    document.addEventListener("keydown", handleShortcuts);
    return () => document.removeEventListener("keydown", handleShortcuts);
  }, []);

  useEffect(() => {
    let inactivityTimer = null;

    const readLastActivity = () => {
      try {
        const n = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
        return Number.isFinite(n) && n > 0 ? n : Date.now();
      } catch (_) {
        return Date.now();
      }
    };

    const writeLastActivity = (ts = Date.now()) => {
      try {
        localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(ts));
      } catch (_) {}
    };

    const remainingIdleMs = () =>
      Math.max(0, SESSION_TIMEOUT_MS - (Date.now() - readLastActivity()));

    const isAppHidden = () =>
      typeof document !== "undefined" && document.visibilityState === "hidden";

    const terminateSession = async () => {
      if (!hasActiveSession()) return;

      const username =
        localStorage.getItem("userEmail") || sessionStorage.getItem("userEmail") || null;
      await deleteFcmTokenOnLogout(username);
      await deleteWebFcmToken();

      clearHomePageInfosClientCache();

      try {
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
      } catch (_) {}
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      localStorage.removeItem("userId");
      sessionStorage.removeItem("userId");
      localStorage.removeItem("isLoggedIn");
      sessionStorage.removeItem("isLoggedIn");
      localStorage.removeItem("userEmail");
      sessionStorage.removeItem("userEmail");
      localStorage.removeItem("registration_step1_data");
      sessionStorage.removeItem("registration_step1_data");
      rsaController.clearUserKeys();
      resetUiPreferencesToDefaults();

      navigate("/home", { replace: true });
      window.location.reload();
    };

    const scheduleInactivityCheck = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = null;
      if (!hasActiveSession()) return;
      if (isAppHidden()) return;

      const remaining = remainingIdleMs();
      if (remaining <= 0) {
        terminateSession();
        return;
      }

      inactivityTimer = setTimeout(() => {
        if (isAppHidden()) return;
        if (remainingIdleMs() <= 0) {
          terminateSession();
          return;
        }
        scheduleInactivityCheck();
      }, remaining);
    };

    const resetInactivityTimer = () => {
      if (!hasActiveSession()) return;
      if (!isAppHidden()) {
        writeLastActivity();
      }
      scheduleInactivityCheck();
    };

    const onVisibilityChange = () => {
      if (!hasActiveSession()) return;
      if (isAppHidden()) {
        if (inactivityTimer) clearTimeout(inactivityTimer);
        inactivityTimer = null;
        return;
      }
      scheduleInactivityCheck();
    };

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onVisibilityChange);
    window.addEventListener("pageshow", onVisibilityChange);
    window.addEventListener("freeze", onVisibilityChange);
    window.addEventListener("resume", onVisibilityChange);

    if (hasActiveSession() && !localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) {
      writeLastActivity();
    }
    scheduleInactivityCheck();

    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onVisibilityChange);
      window.removeEventListener("pageshow", onVisibilityChange);
      window.removeEventListener("freeze", onVisibilityChange);
      window.removeEventListener("resume", onVisibilityChange);
    };
  }, [navigate]);

  return (
    <PageTourProvider>
      <FcmNotificationListener />
      <PwaInstallBanner />
      <KycBackRedirect />
      <WhishReturnHandler />
      <Routes>
        <Route
          path="/tutorial"
          element={<Navigate to="/home" replace />}
        />
        <Route 
          path="/pages/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/pages/marketing-tools"
          element={
            <ProtectedRoute>
              <MarketingTools />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/pages/profile-information" 
          element={
            <ProtectedRoute>
              <ProfileInformation />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/trading-information" 
          element={
            <ProtectedRoute>
              <TradingInformation />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/financial-information" 
          element={
            <ProtectedRoute>
              <FinancialInformation />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/user-regulation" 
          element={
            <ProtectedRoute>
              <UserRegulationPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/cash-in" 
          element={
            <ProtectedRoute>
              <CashIn />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/cash-out" 
          element={
            <ProtectedRoute>
              <CashOut />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/exchange" 
          element={
            <ProtectedRoute>
              <Exchange />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/pages/pay-with-card"
          element={
            <ProtectedRoute>
              <PayWithCard />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/pages/transfer" 
          element={
            <ProtectedRoute>
              <InternalTransfer />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/demo-deposit" 
          element={
            <ProtectedRoute>
              <DepositDemo />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/transactions/history" 
          element={
            <ProtectedRoute>
              <TransactionsHistory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/transactions/cash-in-requests" 
          element={
            <ProtectedRoute>
              <CashInRequest />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/transactions/cash-out-requests" 
          element={
            <ProtectedRoute>
              <CashOutRequest />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/transactions/pending" 
          element={
            <ProtectedRoute>
              <PendingTransactions />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/accounts/list" 
          element={
            <ProtectedRoute>
              <AccountsList hideAccountTypeSwitcher={true} customTitle="Real Accounts" />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/pages/accounts/demo"
          element={
            <ProtectedRoute>
              <DemoAccounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/accounts/history/:tradingId"
          element={
            <ProtectedRoute>
              <AccountHistory />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/pages/ibDashboard/dashboard" 
          element={
            <ProtectedRoute>
              <IbDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/ibDashboard/clients-list" 
          element={
            <ProtectedRoute>
              <IbClientsList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/ibDashboard/clients-tree" 
          element={
            <ProtectedRoute>
              <IbClientsTree />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/ibDashboard/client-accounts/:userId" 
          element={
            <ProtectedRoute>
              <IbClientsAccounts />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/ibDashboard/transfer-to-client" 
          element={
            <ProtectedRoute>
              <TransferToClient />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/ibDashboard/transfer-from-client" 
          element={
            <ProtectedRoute>
              <TransferFromClient />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/pages/ibDashboard/collect-transaction"
          element={
            <ProtectedRoute>
              <CollectTransaction />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/ibDashboard/send-notification"
          element={
            <ProtectedRoute>
              <SendNotification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/ibDashboard/broker-tools/editor"
          element={
            <ProtectedRoute>
              <BrokerToolEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/ibDashboard/broker-tools"
          element={
            <ProtectedRoute>
              <IbBrokerTools />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/pages/settings/change-pin" 
          element={
            <ProtectedRoute>
              <ChangePin />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pages/settings/active-devices" 
          element={
            <ProtectedRoute>
              <ActiveDevices />
            </ProtectedRoute>
          } 
        />
        {/* <Route
          path="/pages/settings/delete-account"
          element={
            <ProtectedRoute>
              <DeleteAccount />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/pages/legal/privacy-policy"
          element={
            <ProtectedRoute>
              <LegalDocumentPage documentKey="privacy_policy" title="Privacy Policy" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/legal/terms-of-services"
          element={
            <ProtectedRoute>
              <LegalDocumentPage documentKey="terms_of_services" title="Terms of Services" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/legal/risk-disclosure"
          element={
            <ProtectedRoute>
              <LegalDocumentPage documentKey="risk_disclosure" title="Risk Disclosure" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/support/faq"
          element={
            <ProtectedRoute>
              <SupportFaq />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pages/support/about-us"
          element={
            <ProtectedRoute>
              <SupportAboutUs />
            </ProtectedRoute>
          }
        />
        <Route path="/*" element={
          <Background>
            <LanguageSwitcher />
            <Routes>
              <Route
                path="/"
                element={<Home />}
              />
              <Route
                path="/home"
                element={<Home />}
              />
              <Route path="/informationAccount/:broker_id?" element={<InformationAccount />} />
              <Route path="/personalInformation" element={<PersonalInformation />} />
              <Route path="/registrationEmail" element={<RegistrationEmail />} />
              <Route path="/forget-password" element={<ForgetPassword />} />
              <Route path="/device-verification" element={<DeviceVerification />} />
              <Route path="/agreement" element={<AgreementPage />} />
            </Routes>
          </Background>
        } />
      </Routes>
    </PageTourProvider>
  );
}
